// D:\Casticart\frontend\src\components\common\chat\chatApi.js
import {
  sendAdminFileMessage,
  sendAdminImageMessages,
  sendAdminTextMessage,
} from '../../../../api/adminChat';
import {
  sendAdminGroupChatFileMessage,
  sendAdminGroupChatImageMessages,
  sendAdminGroupChatTextMessage,
} from '../../../../api/adminGroupChat';
import {
  sendArtistFileMessage,
  sendArtistGroupChatFileMessage,
  sendArtistGroupChatImageMessages,
  sendArtistGroupChatTextMessage,
  sendArtistImageMessages,
  sendArtistTextMessage,
} from '../../../../api/artistChat';
import {
  sendEventChatFileMessage,
  sendEventChatImageMessages,
  sendEventChatTextMessage,
} from '../../../../api/eventChat';

const API_URL = process.env.REACT_APP_API_URL; // 例: http://localhost:5001

function assertApiUrl() {
  if (!API_URL) {
    throw new Error('REACT_APP_API_URL is not defined');
  }
}

export function createClientMessageId() {
  const randomPart =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `web-${randomPart}`.replace(/[^A-Za-z0-9-]/g, '').slice(0, 64);
}

// --- Text -------------------------------------------------------------------
/**
 * mode: 'admin' | 'artist'
 * scope: 'individual' | 'event' | 'group'
 * - individual: 既存の個別チャットエンドポイント /api/{mode}/messages/text
 * - event:      新イベントチャットエンドポイント  /api/events/:eventId/messages/text
 * - group:      グループチャットエンドポイント /api/{mode}/group-chat/threads/:groupThreadId/messages/text
 */
export async function sendText({
  mode = 'admin',
  scope = 'individual',
  artistId,
  eventId,
  groupThreadId,
  content,
  mentions,
  senderId, // 任意：バック側でセッション補完されるが、渡せるなら渡す
  replyToMessageId,
  notificationDeliveryMode,
  clientMessageId: providedClientMessageId,
}) {
  assertApiUrl();
  const clientMessageId = providedClientMessageId || createClientMessageId();

  if (scope === 'group') {
    if (!groupThreadId) throw new Error('groupThreadId is required');
    const body = { content, senderId, replyToMessageId, mentions, notificationDeliveryMode, clientMessageId };
    return mode === 'artist'
      ? sendArtistGroupChatTextMessage(groupThreadId, body, clientMessageId)
      : sendAdminGroupChatTextMessage(groupThreadId, body, clientMessageId);
  }

  if (scope === 'event') {
    return sendEventChatTextMessage(eventId, {
      content,
      senderType: mode === 'admin' ? 'admin' : 'artist',
      senderId,
      replyToMessageId,
      mentions,
      notificationDeliveryMode,
      clientMessageId,
    }, clientMessageId);
  }

  // 既存：個別チャット
  const body = { artistId, content, replyToMessageId, mentions, notificationDeliveryMode, clientMessageId };
  return mode === 'artist'
    ? sendArtistTextMessage(body, clientMessageId)
    : sendAdminTextMessage(body, clientMessageId);
}

// --- Images -----------------------------------------------------------------
/**
 * scope=event の場合は /api/events/:eventId/messages/images
 * scope=individual の場合は /api/{mode}/messages/images
 * scope=group の場合は /api/{mode}/group-chat/threads/:groupThreadId/messages/images
 * 変更点：event の場合でも multipart に senderType / senderId を必ず付与
 */
export async function sendImages({
  mode = 'admin',
  scope = 'individual',
  eventId,
  groupThreadId,
  files,
  extraFields,
  senderId, // ★追加
  replyToMessageId,
  notificationDeliveryMode,
}) {
  assertApiUrl();
  const fd = new FormData();
  (files || []).forEach((f) => fd.append('images', f));

  if (scope === 'group') {
    if (!groupThreadId) throw new Error('groupThreadId is required');
    if (senderId) fd.append('senderId', String(senderId));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return mode === 'artist'
      ? sendArtistGroupChatImageMessages(groupThreadId, fd)
      : sendAdminGroupChatImageMessages(groupThreadId, fd);
  } else if (scope === 'event') {
    // 明示的に送信者を付与（同時ログインでも誤判定しない）
    fd.append('senderType', mode === 'admin' ? 'admin' : 'artist');
    if (senderId) fd.append('senderId', String(senderId));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return sendEventChatImageMessages(eventId, fd);
  } else {
    Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return mode === 'artist' ? sendArtistImageMessages(fd) : sendAdminImageMessages(fd);
  }
}

// --- File -------------------------------------------------------------------
/**
 * scope=event の場合は /api/events/:eventId/messages/files
 * scope=individual の場合は /api/{mode}/messages/files
 * scope=group の場合は /api/{mode}/group-chat/threads/:groupThreadId/messages/files
 * 変更点：event の場合でも multipart に senderType / senderId を必ず付与
 */
export async function sendFile({
  mode = 'admin',
  scope = 'individual',
  eventId,
  groupThreadId,
  file,
  extraFields,
  senderId, // ★追加
  replyToMessageId,
  notificationDeliveryMode,
}) {
  assertApiUrl();
  const fd = new FormData();
  fd.append('file', file);

  if (scope === 'group') {
    if (!groupThreadId) throw new Error('groupThreadId is required');
    if (senderId) fd.append('senderId', String(senderId));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return mode === 'artist'
      ? sendArtistGroupChatFileMessage(groupThreadId, fd)
      : sendAdminGroupChatFileMessage(groupThreadId, fd);
  } else if (scope === 'event') {
    fd.append('senderType', mode === 'admin' ? 'admin' : 'artist');
    if (senderId) fd.append('senderId', String(senderId));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return sendEventChatFileMessage(eventId, fd);
  } else {
    Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
    if (replyToMessageId) fd.append('replyToMessageId', String(replyToMessageId));
    if (notificationDeliveryMode) fd.append('notificationDeliveryMode', String(notificationDeliveryMode));
    return mode === 'artist' ? sendArtistFileMessage(fd) : sendAdminFileMessage(fd);
  }
}
